import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  isSessionValid,
  storeAuthData,
  syncCookiesToStorage,
} from "@/utils/auth";
import logger from "@/utils/logger";

export default function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(true); // Default to true to prevent flash
  const navigate = useNavigate();
  const lastCheckTimeRef = useRef(Date.now());

  // Function to check auth and redirect if needed
  const checkAuthAndRedirect = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const previewToken = urlParams.get("preview_token");

    // Preview mode is sticky for the tab session: once entered via a
    // preview_token link, keep bypassing auth for the rest of the session even
    // if a later navigation drops the query param — so an internal preview link
    // reliably lands (and stays) on the target page without bouncing to login.
    if (previewToken) {
      sessionStorage.setItem("previewMode", "1");
    }
    if (previewToken || sessionStorage.getItem("previewMode") === "1") {
      logger.log("Preview mode active. Bypassing auth check.");
      setIsAuthenticated(true);
      return true;
    }

    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckTimeRef.current;

    // Update last check time
    lastCheckTimeRef.current = now;

    // Sync any auth cookies from magic link subdomain before checking validity
    syncCookiesToStorage();

    // Perform the validation
    const valid = isSessionValid();

    if (!valid) {
      setIsAuthenticated(false);
      // Force navigate to login
      navigate("/login", { replace: true });
      return false;
    }
    return true;
  };

  useEffect(() => {
    // Set up visibility change listener to detect when user returns to tab
    // This helps catch time changes when the tab was in the background
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAuthAndRedirect();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Always check on initial mount
    const valid = checkAuthAndRedirect();
    if (!valid) return;

    // Transfer sessionStorage to localStorage if needed
    const sessionSid = sessionStorage.getItem("verificationSid");
    if (sessionSid) {
      const userEmail = sessionStorage.getItem("userEmail");
      const phoneNumber = sessionStorage.getItem("phoneNumber");
      const authToken = sessionStorage.getItem("authToken");
      // onboarding will be extracted from authToken automatically
      storeAuthData(sessionSid, userEmail, phoneNumber, authToken, 72);
    }

    // Set up interval for regular checks (every 10 seconds)
    const intervalId = setInterval(() => {
      checkAuthAndRedirect();
    }, 10000); // Check every 10 seconds for better responsiveness

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []); // Only run once on mount

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}
